import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  buildCartSectionsUrl,
  isCartPanelOpen,
  syncCartPanelFromSource,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  CartSectionsMorphedEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { getCartLineErrorElements } from '@theme/cart-line-errors';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #quantityUpdateRequestId = 0;

  /** Serializes cart/change requests so rapid removes cannot abort each other. */
  /** @type {Promise<void>} */
  static #cartUpdateQueue = Promise.resolve();

  /** Locks all cart-items surfaces during qty changes (not pending removes). */
  /** @type {number} */
  static #cartMutationLockCount = 0;

  /** Line item keys queued for removal — rows stay in DOM until server confirms. */
  /** @type {Set<string>} */
  static #pendingRemovalKeys = new Set();

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
      key: this.#getLineKey(line),
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number | { key: string }} lineOrKey - Line index (legacy) or stable line item key.
   */
  onLineItemRemove(lineOrKey) {
    const key = this.#resolveRemoveKey(lineOrKey);
    const line = typeof lineOrKey === 'number' ? lineOrKey : this.#getLineNumberForKey(key);

    if (key && CartItemsComponent.#pendingRemovalKeys.has(key)) return;

    const cartItemRowToRemove = key
      ? this.#findRowByKey(key)
      : line
        ? this.refs.cartItemRows?.[line - 1]
        : undefined;

    const resolvedKey = key ?? (cartItemRowToRemove instanceof HTMLElement ? cartItemRowToRemove.dataset.key : undefined);

    if (!resolvedKey && !cartItemRowToRemove) return;

    if (resolvedKey) {
      CartItemsComponent.#pendingRemovalKeys.add(resolvedKey);
    }

    if (cartItemRowToRemove instanceof HTMLElement) {
      const rowsToMark = this.#getRowsForKeyIncludingNested(resolvedKey ?? cartItemRowToRemove.dataset.key);
      rowsToMark.forEach((row) => this.#markRowPending(row));
    }

    this.updateQuantity({
      line: line ?? 0,
      quantity: 0,
      action: 'clear',
      key: resolvedKey,
    });
  }

  /**
   * @param {number | { key: string }} lineOrKey
   * @returns {string | undefined}
   */
  #resolveRemoveKey(lineOrKey) {
    if (lineOrKey && typeof lineOrKey === 'object' && 'key' in lineOrKey) {
      return String(lineOrKey.key);
    }

    if (typeof lineOrKey === 'number') {
      return this.#getLineKey(lineOrKey);
    }

    return undefined;
  }

  /**
   * @param {string | undefined} key
   * @returns {HTMLElement | undefined}
   */
  #findRowByKey(key) {
    if (!key) return undefined;

    const rows = this.refs.cartItemRows;
    if (!Array.isArray(rows)) return undefined;

    return rows.find((row) => row instanceof HTMLElement && row.dataset.key === key);
  }

  /**
   * @param {string | undefined} key
   * @returns {HTMLElement[]}
   */
  #getRowsForKeyIncludingNested(key) {
    if (!key) return [];

    const primary = this.#findRowByKey(key);
    if (!primary) return [];

    const childRows = this.refs.cartItemRows.filter(
      (row) => row instanceof HTMLElement && row.dataset.parentKey === key
    );

    return [primary, ...childRows];
  }

  /**
   * @param {string | undefined} key
   * @returns {number | undefined}
   */
  #getLineNumberForKey(key) {
    if (!key) return undefined;

    const rows = this.refs.cartItemRows;
    if (!Array.isArray(rows)) return undefined;

    const index = rows.findIndex((row) => row instanceof HTMLElement && row.dataset.key === key);
    return index >= 0 ? index + 1 : undefined;
  }

  /**
   * @param {number} line
   * @returns {string | undefined}
   */
  #getLineKey(line) {
    const row = this.refs.cartItemRows?.[line - 1];
    return row instanceof HTMLElement ? row.dataset.key : undefined;
  }

  /**
   * Greys out a row while its removal request is in flight.
   * @param {HTMLElement} row
   */
  #markRowPending(row) {
    row.classList.add('is-pending-remove');
    this.#disableRowControls(row);
  }

  /**
   * @param {HTMLElement} row
   */
  #disableRowControls(row) {
    row.querySelectorAll(
      '.right-panel__cart-item-remove, .cart-items__remove, cart-quantity-selector-component button, input[type="number"]'
    ).forEach((el) => {
      if (el instanceof HTMLButtonElement || el instanceof HTMLInputElement) {
        el.disabled = true;
      }
    });
  }

  /**
   * @param {HTMLElement} row
   */
  #enableRowControls(row) {
    row.querySelectorAll(
      '.right-panel__cart-item-remove, .cart-items__remove, cart-quantity-selector-component button, input[type="number"]'
    ).forEach((el) => {
      if (el instanceof HTMLButtonElement || el instanceof HTMLInputElement) {
        el.disabled = false;
      }
    });
  }

  /**
   * Reverts pending UI when a removal request fails.
   * @param {string | undefined} key
   */
  #revertPendingRemove(key) {
    if (!key) return;

    CartItemsComponent.#pendingRemovalKeys.delete(key);

    for (const row of this.#getRowsForKeyIncludingNested(key)) {
      row.classList.remove('is-pending-remove');
      this.#enableRowControls(row);
    }
  }

  /**
   * Re-applies pending markers after a section morph.
   */
  #restorePendingRowMarkers() {
    for (const pendingKey of CartItemsComponent.#pendingRemovalKeys) {
      for (const row of this.#getRowsForKeyIncludingNested(pendingKey)) {
        row.classList.add('is-pending-remove');
        this.#disableRowControls(row);
      }
    }
  }

  /**
   * Plays exit animation and removes rows from the DOM after server confirms removal.
   * @param {HTMLElement[]} rows
   * @returns {Promise<void>}
   */
  #animateRowsOutAndRemove(rows) {
    const uniqueRows = [...new Set(rows)];

    return Promise.all(
      uniqueRows.map(
        (row) =>
          new Promise((resolve) => {
            const remove = () => {
              row.remove();
              resolve(undefined);
            };

            if (prefersReducedMotion()) {
              remove();
              return;
            }

            row.style.setProperty('--row-height', `${row.clientHeight}px`);
            row.classList.add('removing');
            onAnimationEnd(row, remove);
          })
      )
    );
  }

  /**
   * Removes still-pending line items from section HTML so morph cannot re-insert them.
   * @param {string | undefined} sectionHtml
   * @returns {string | undefined}
   */
  #stripPendingRowsFromSectionHtml(sectionHtml) {
    if (!sectionHtml || CartItemsComponent.#pendingRemovalKeys.size === 0) return sectionHtml;

    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const pendingKeys = CartItemsComponent.#pendingRemovalKeys;

    doc
      .querySelectorAll('.right-panel__cart-item[data-key], .cart-items__table-row[data-key]')
      .forEach((row) => {
        if (row instanceof HTMLElement && pendingKeys.has(row.dataset.key ?? '')) {
          row.remove();
        }
      });

    return doc.body.innerHTML;
  }

  /**
   * Broadcasts the cart count so bubbles and badges update immediately.
   * @param {number} itemCount
   * @param {Record<string, string>} [sections]
   * @param {Record<string, unknown>} [resource]
   */
  #publishCartCount(itemCount, sections, resource) {
    document.dispatchEvent(
      new CartUpdateEvent(resource ?? { item_count: itemCount }, this.sectionId, {
        itemCount,
        source: 'cart-items-component',
        sections,
      })
    );
  }

  /**
   * @param {Record<string, unknown>} parsedResponseText
   * @param {Document} sectionDocument
   * @returns {number}
   */
  #resolveCartItemCount(parsedResponseText, sectionDocument) {
    const fromResponse = Number(parsedResponseText.item_count);
    if (Number.isFinite(fromResponse)) return fromResponse;

    const fromSection = sectionDocument.querySelector('[ref="cartItemCount"]')?.textContent;
    const parsed = parseInt(fromSection ?? '0', 10);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {string} [config.key] - Stable line item key (preferred over line index).
   */
  updateQuantity(config) {
    CartItemsComponent.#cartUpdateQueue = CartItemsComponent.#cartUpdateQueue
      .then(() => this.#performQuantityUpdate(config))
      .catch((error) => {
        console.error(error);
      });

    return CartItemsComponent.#cartUpdateQueue;
  }

  /**
   * @param {Object} config
   * @param {number} config.line
   * @param {number} config.quantity
   * @param {string} config.action
   * @param {string} [config.key]
   */
  async #performQuantityUpdate(config) {
    window.__eaCartMorphGen = (window.__eaCartMorphGen ?? 0) + 1;

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    const requestId = ++this.#quantityUpdateRequestId;
    const isClearAction = config.action === 'clear';

    const { line, quantity, key } = config;
    this.#clearCartItemError(line, key);

    if (!isClearAction) {
      this.#disableCartItems();
    }

    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    /** @type {Record<string, unknown>} */
    const payload = {
      quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: buildCartSectionsUrl(),
    };

    if (key) {
      payload.id = key;
    } else {
      payload.line = line;
    }

    if (typeof cartTotal?.shimmer === 'function') {
      cartTotal.shimmer();
    }

    try {
      const response = await fetch(`${Theme.routes.cart_change_url}`, {
        ...fetchConfig('json', { body: JSON.stringify(payload) }),
      });
      const responseText = await response.text();

      if (requestId !== this.#quantityUpdateRequestId) {
        return;
      }

      const parsedResponseText = JSON.parse(responseText);

      resetShimmer(this);

      const errorMessage = this.#getCartChangeErrorMessage(parsedResponseText);
      if (errorMessage) {
        if (isClearAction) {
          this.#revertPendingRemove(key);
        }
        this.#handleCartError(line, errorMessage, key);
        return;
      }

      const newSectionHTML = new DOMParser().parseFromString(
        parsedResponseText.sections[this.sectionId],
        'text/html'
      );

      const resolvedItemCount = this.#resolveCartItemCount(parsedResponseText, newSectionHTML);

      this.#updateQuantitySelectors(parsedResponseText);

      const cartResource = {
        ...parsedResponseText,
        item_count: resolvedItemCount,
      };

      if (isClearAction && key) {
        CartItemsComponent.#pendingRemovalKeys.delete(key);
        await this.#animateRowsOutAndRemove(this.#getRowsForKeyIncludingNested(key));
      }

      this.#publishCartCount(resolvedItemCount, parsedResponseText.sections, cartResource);

      const morphMode = this.usesHydrationMorph ? 'hydration' : 'full';

      let sectionHtml = parsedResponseText.sections?.[this.sectionId];
      if (sectionHtml) {
        sectionHtml = this.#stripPendingRowsFromSectionHtml(sectionHtml);
        await morphSection(this.sectionId, sectionHtml, {
          mode: morphMode,
        });
        this.#restorePendingRowMarkers();
        if (isCartPanelOpen()) {
          syncCartPanelFromSource();
          this.dispatchEvent(
            new CartSectionsMorphedEvent(this.sectionId, { source: 'cart-items-component' })
          );
        }
      }

      this.#updateCartQuantitySelectorButtonStates();
    } catch (error) {
      if (isClearAction) {
        this.#revertPendingRemove(key);
      }
      console.error(error);
    } finally {
      if (requestId === this.#quantityUpdateRequestId && !isClearAction) {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      } else if (requestId === this.#quantityUpdateRequestId) {
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      }
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Extracts a user-facing error message from a cart change response.
   * @param {Record<string, unknown>} parsedResponseText
   * @returns {string | null}
   */
  #getCartChangeErrorMessage(parsedResponseText) {
    if (parsedResponseText.errors) {
      if (typeof parsedResponseText.errors === 'string') return parsedResponseText.errors;

      if (typeof parsedResponseText.errors === 'object' && parsedResponseText.errors !== null) {
        return Object.values(parsedResponseText.errors).flat().join(' ');
      }
    }

    if (parsedResponseText.status === 422) {
      if (typeof parsedResponseText.message === 'string') return parsedResponseText.message;
      if (typeof parsedResponseText.description === 'string') return parsedResponseText.description;
    }

    return null;
  }

  /**
   * Finds inline error elements for a cart line.
   * @param {number} line
   * @param {string} [key]
   * @returns {{ errorText: HTMLElement | null, errorContainer: HTMLElement | null, quantityInput: HTMLInputElement | null, row: HTMLElement | null }}
   */
  #getLineElements(line, key) {
    const row =
      (key ? this.#findRowByKey(key) : undefined) ??
      (this.refs.cartItemRows?.[line - 1] instanceof HTMLElement
        ? this.refs.cartItemRows[line - 1]
        : undefined);

    if (!(row instanceof HTMLElement)) {
      return { errorText: null, errorContainer: null, quantityInput: null, row: null };
    }

    const lineIndex = this.refs.cartItemRows.indexOf(row) + 1;
    const { errorText, errorContainer } = getCartLineErrorElements(row, lineIndex || line);
    const quantityInput = row.querySelector('cart-quantity-selector-component input[type="number"]');

    return {
      errorText,
      errorContainer,
      quantityInput: quantityInput instanceof HTMLInputElement ? quantityInput : null,
      row,
    };
  }

  /**
   * Shows an inline error for a cart line.
   * @param {number} line
   * @param {string} errorMessage
   * @param {string} [key]
   */
  #showCartItemError(line, errorMessage, key) {
    const { errorText, errorContainer } = this.#getLineElements(line, key);

    if (!(errorText instanceof HTMLElement) || !(errorContainer instanceof HTMLElement)) return;

    errorText.textContent = errorMessage;
    errorContainer.classList.remove('hidden');
  }

  /**
   * Clears the inline error for a cart line.
   * @param {number} line
   * @param {string} [key]
   */
  #clearCartItemError(line, key) {
    const { errorText, errorContainer } = this.#getLineElements(line, key);

    if (errorText instanceof HTMLElement) {
      errorText.textContent = '';
    }

    if (errorContainer instanceof HTMLElement) {
      errorContainer.classList.add('hidden');
    }
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {string} errorMessage - The error message to display.
   * @param {string} [key] - Stable line item key.
   */
  #handleCartError = (line, errorMessage, key) => {
    const { quantityInput } = this.#getLineElements(line, key);
    const selector = quantityInput?.closest('cart-quantity-selector-component');
    const displayMessage = selector?.dataset.maxQuantityMessage || errorMessage;

    this.#showCartItemError(line, displayMessage, key);

    if (!quantityInput) return;

    quantityInput.value = quantityInput.defaultValue;

    if (selector && 'updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
      selector.updateButtonStates();
    }
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      if (event.detail.resource?.sections?.[this.sectionId]) {
        this.#updateCartQuantitySelectorButtonStates();
        return;
      }

      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const eventSource = event.detail.data?.source;
    const morphAlreadyHandled =
      eventSource === 'product-form-component' || eventSource === 'cart-items-component';

    if (morphAlreadyHandled) {
      this.#updateCartQuantitySelectorButtonStates();
      return;
    }

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, this.#stripPendingRowsFromSectionHtml(cartItemsHtml), {
        mode: this.usesHydrationMorph ? 'hydration' : 'full',
      }).then(() => {
        this.#restorePendingRowMarkers();
        if (isCartPanelOpen()) {
          syncCartPanelFromSource();
          this.dispatchEvent(
            new CartSectionsMorphedEvent(this.sectionId, { source: 'cart-items-component' })
          );
        }
      });

      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, {
        cache: false,
        mode: this.usesHydrationMorph ? 'hydration' : 'full',
      });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    CartItemsComponent.#cartMutationLockCount++;
    document.querySelectorAll('cart-items-component').forEach((el) => {
      el.classList.add('cart-items-disabled');
    });
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    if (CartItemsComponent.#cartMutationLockCount > 0) {
      CartItemsComponent.#cartMutationLockCount--;
    }

    if (CartItemsComponent.#cartMutationLockCount === 0) {
      document.querySelectorAll('cart-items-component').forEach((el) => {
        el.classList.remove('cart-items-disabled');
      });
    }
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }

  /**
   * @returns {boolean} Whether the component is the desktop right-panel cart.
   */
  get isPanel() {
    return this.dataset.panel !== undefined;
  }

  /**
   * @returns {boolean} Whether section updates should use hydration morph.
   */
  get usesHydrationMorph() {
    return this.isDrawer || this.isPanel;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
